import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Link } from '@mui/material';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import { ProgressCardItem as ProgressCardItemType, GenerateProgressCardItemProps } from '../types';
import { ProgressDetailsMarkupMAIQ } from 'app/NewAIAvatarLayout/sharedComponents/ProgressDetailsMarkupMAIQ';

const ItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: string }>(({ theme, variant }) => {
  const getVariantStyles = (variant: string) => {
    const isDark = theme.palette.mode === 'dark';
    
    switch (variant) {
      case 'purple':
        return {
          backgroundColor: isDark ? 'rgba(147, 51, 234, 0.15)' : 'rgba(147, 51, 234, 0.1)',
        };
      case 'blue':
        return {
          backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
        };
      case 'green':
        return {
          backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
        };
      case 'red':
        return {
          backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
        };
      case 'orange':
        return {
          backgroundColor: isDark ? 'rgba(249, 115, 22, 0.15)' : 'rgba(249, 115, 22, 0.1)',
        };
      default:
        return {
          backgroundColor: isDark ? 'rgba(107, 114, 128, 0.15)' : 'rgba(107, 114, 128, 0.1)',
        };
    }
  };

  return {
    padding: theme.spacing(2),
    borderRadius: '8px',
    ...getVariantStyles(variant),
    '&:last-child': {
      marginBottom: 0,
    },
  };
});

const HeaderText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '14px',
  fontWeight: 400,
}));

const DetailsText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.4,
}));

const NumberValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '2.5rem',
  fontWeight: 700,
  lineHeight: 1,
}));

const ListContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const ListItem = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: 400,
  marginBottom: theme.spacing(0.25),
  paddingLeft: theme.spacing(1),
  position: 'relative',
  '&::before': {
    content: '"•"',
    position: 'absolute',
    left: 0,
    color: theme.palette.text.primary,
  },
  '&:last-child': {
    marginBottom: 0,
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: '8px',
  overflow: 'hidden',
  maxWidth: '100%',
  height: 'auto',
}));

const StyledImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '300px',
  objectFit: 'contain',
  borderRadius: '8px',
}));

const ImageFallbackContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  minHeight: '140px',
  borderRadius: '8px',
  border: `1px dashed ${theme.palette.divider}`,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(2),
}));

const ImageFallback = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const ImageFallbackLink = styled(Link)(({ theme }) => ({
  display: 'inline-block',
  marginTop: theme.spacing(1),
  fontSize: '0.75rem',
  fontWeight: 600,
  wordBreak: 'break-all',
}));

export const GenerateProgressCardItem: React.FC<GenerateProgressCardItemProps> = ({ item }) => {
  const { header, details, variant = 'default', img_url } = item;
  const [hasImageError, setHasImageError] = useState(false);
  
  // Determine if this is a number value (for audience count)
  const isNumberValue = header?.toLowerCase().includes('count') || 
                       header?.toLowerCase().includes('number') ||
                       /^\d+$/.test(details || '');
  
  // Determine if this is a list (for filters)
  const isListValue = Array.isArray(details) || 
                     (typeof details === 'string' && details.includes('\n')) ||
                     header?.toLowerCase().includes('filter');

  const renderValue = () => {
    // If img_url is present, show image unless it fails to load.
    if (img_url && !hasImageError) {
      return (
        <ImageContainer>
          <StyledImage
            src={img_url}
            alt={header || 'Progress item image'}
            onError={(e) => {
              console.error('Failed to load image:', img_url);
              setHasImageError(true);
            }}
          />
        </ImageContainer>
      );
    }

    if (img_url && hasImageError) {
      return (
        <ImageFallbackContainer>
          <Box>
            <BrokenImageOutlinedIcon sx={{ color: 'text.secondary', fontSize: 28, mb: 0.5 }} />
            <ImageFallback>Image preview is not available</ImageFallback>
            <ImageFallbackLink
              href={img_url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Open image URL
            </ImageFallbackLink>
          </Box>
        </ImageFallbackContainer>
      );
    }

    if (isNumberValue) {
      return (
        <ProgressDetailsMarkupMAIQ 
          content={details || '0'} 
          variant="large" 
        />
      );
    }
    
    if (isListValue && typeof details === 'string') {
      return (
        <ProgressDetailsMarkupMAIQ 
          content={details} 
          variant="default" 
        />
      );
    }
    
    return (
      <ProgressDetailsMarkupMAIQ 
        content={details || ''} 
        variant="default" 
      />
    );
  };

  if (!header && !details && !img_url) {
    return null;
  }

  return (
    <ItemContainer variant={variant}>
      {header && (
        <HeaderText>
          {header}:
        </HeaderText>
      )}
      {renderValue()}
    </ItemContainer>
  );
};

